import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

export const ProductSchema = z.object({
  code: z.string(),
  stars_price: z.number().int().nonnegative(),
  grants: z.record(z.number().int()),
  is_active: z.boolean(),
  display_order: z.number().int(),
  title_ru: z.string().nullable(),
  title_en: z.string().nullable(),
  title_es: z.string().nullable(),
  description_ru: z.string().nullable(),
  description_en: z.string().nullable(),
  description_es: z.string().nullable(),
  updated_at: z.string(),
});
export type Product = z.infer<typeof ProductSchema>;
const ProductListSchema = z.array(ProductSchema);

const KEY = ["admin", "products"];

export function useProducts() {
  return useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) =>
      request({
        path: "/api/admin/products?include_inactive=true",
        schema: ProductListSchema,
        signal,
      }),
  });
}

export function usePatchProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { code: string; patch: Partial<Product> }) =>
      request({
        method: "PATCH",
        path: `/api/admin/products/${args.code}`,
        body: args.patch,
        schema: ProductSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<Product, "updated_at">) =>
      request({
        method: "POST",
        path: "/api/admin/products",
        body,
        schema: ProductSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
